import React, { useContext, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { FaCloudUploadAlt, FaTimes, FaFileImage, FaFileVideo } from 'react-icons/fa';
import { ToastSuccess, ToastError } from '../functions/ToastShow';
import { BASE_URL } from '../BaseUrl';
import { SettingsContext } from '../contexts/Settings';
import { _t, getCookie } from '../functions/Functions';

const MediaUpload = (props) => {
  const { t } = useTranslation();
  const { loading, setLoading } = useContext(SettingsContext);

  const {
    apiUrl = "",
    uploadImage,
    closeModal,
    uploadBtnName = "Save",
    allowVideo = false,
    onSuccess = () => { },
    acceptTypes = allowVideo ? "image/*,video/*" : "image/*"
  } = props;

  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [fileType, setFileType] = useState(""); // image or video

  const processFile = (file) => {
    if (!file) return;

    // Validation
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');

    if (isImage || (allowVideo && isVideo)) {
      setSelectedFile(file);
      setFileType(isImage ? "image" : "video");

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      ToastError({ title: t(allowVideo ? "Please upload an image or video file" : "Please upload an image file") });
    }
  };

  const handleOnChange = (e) => {
    if (e.target.files[0]) {
      processFile(e.target.files[0]);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFile(e.dataTransfer.files[0]);
      e.dataTransfer.clearData();
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setFileType("");
    if (document.getElementById('mediaFileInput')) {
      document.getElementById('mediaFileInput').value = '';
    }
  };

  const handleUpload = (e) => {
    e.preventDefault();
    if (!selectedFile) {
      ToastError({ title: t("Please select a file first") });
      return;
    }

    if (!apiUrl) {
      console.error("MediaUpload: apiUrl prop is required for uploading.");
      return;
    }

    const url = BASE_URL + apiUrl;
    const formData = new FormData();
    formData.append('food_item_id', uploadImage.id);
    formData.append('image', selectedFile);

    setLoading(true);
    axios.post(url, formData, {
      headers: {
        'Authorization': `Bearer ${getCookie()}`,
        'Content-Type': 'multipart/form-data'
      }
    }).then((res) => {
      setLoading(false);
      ToastSuccess({ title: t("Uploaded successfully") });
      removeFile();
      onSuccess(res.data);
    }).catch((err) => {
      setLoading(false);
      const errorMessage = err.response?.data?.message || t("Upload failed");
      ToastError({ title: _t(errorMessage) });
    });
  };

  return (
    <>
      {/*upload csv Modal*/ }
      <div
        className = {`modal fade ${uploadImage.modal ? "showing" : ""
        }`}
        id = "addReason"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-md">
          <div className="modal-content">
            <div className="modal-header align-items-center">
              <div className="fk-sm-card__content font-weight-md">Upload Image</div>
              <button
                type="button"
                className="btn-close"
                data-dismiss="modal"
                aria-label="Close"
                onClick={(e) => {
                  closeModal();
                  removeFile()
                }}
              ></button>
            </div>
            <div className="modal-body">
              {/* show form or show saving loading */}
              <div key="fragment-food-group-1">
                <div className="media-upload-container">
                  <form onSubmit={handleUpload}>
                    {!selectedFile ? (
                      <div
                        className={`dropzone ${isDragging ? 'dragging' : ''}`}
                        onDragEnter={handleDragEnter}
                        onDragLeave={handleDragLeave}
                        onDragOver={handleDragOver}
                        onDrop={handleDrop}
                        onClick={() => document.getElementById('mediaFileInput').click()}
                        style={{
                          border: `2px dashed ${isDragging ? '#28a745' : '#ced4da'}`,
                          borderRadius: '10px',
                          padding: '30px 20px',
                          textAlign: 'center',
                          cursor: 'pointer',
                          backgroundColor: isDragging ? '#f1f8f1' : '#f8f9fa',
                          transition: 'all 0.3s ease',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minHeight: '150px'
                        }}
                      >
                        <FaCloudUploadAlt size={40} color={isDragging ? '#28a745' : '#6c757d'} style={{ marginBottom: '10px' }} />
                        <p className="mb-0 text-muted" style={{ fontWeight: '500' }}>
                          {isDragging ? t("Drop to upload") : t("Drag & Drop or click to browse")}
                        </p>
                        <small className="text-muted mt-1">
                          {allowVideo ? t("Supports images and videos") : t("Supports images only")}
                        </small>
                        <input
                          type="file"
                          id="mediaFileInput"
                          accept={acceptTypes}
                          onChange={handleOnChange}
                          style={{ display: 'none' }}
                        />
                      </div>
                    ) : (
                      <div className="preview-container p-3 border rounded bg-light position-relative">
                        <div className="d-flex align-items-center">
                          <div className="media-preview mr-3" style={{ width: '50px', height: '50px', overflow: 'hidden', borderRadius: '4px', backgroundColor: '#e9ecef', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {fileType === "image" ? (
                              <img src={previewUrl} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            ) : (
                              <FaFileVideo size={30} className="text-secondary" />
                            )}
                          </div>
                          <div className="flex-grow-1 overflow-hidden">
                            <span className="d-block font-weight-bold text-truncate" title={selectedFile.name}>{selectedFile.name}</span>
                            <small className="text-muted">{(selectedFile.size / (1024 * 1024)).toFixed(2)} MB</small>
                          </div>
                          <button
                            type="button"
                            className="btn btn-outline-danger rounded-circle ml-2"
                            onClick={removeFile}
                          >
                            <FaTimes />
                          </button>
                        </div>

                        <div className="mt-4 text-center">
                          <button
                            type="submit"
                            className="btn btn-primary btn-sm px-4 rounded"
                            disabled={loading}
                          >
                            {uploadBtnName}
                          </button>
                        </div>
                      </div>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  
  );
};

export default React.memo(MediaUpload);
